import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';

//COMPONENTS
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';

//REDUX/UTILS
import { KOGAreaButtonsModal } from '@/main/components/shared/styles';
import { getDefaultCountryOptionDrop, getTranslateByKey, isEmpty, isNullUndefined } from '@/main/services/utils';

// STYLES/COMPONENTS LOCAL
import { procurementApi } from '@/main/api';
import { GetBulkChangeItemsByClaimRequest, ProcurementProcessStatusBulkRequest, ReasonFilterRequest } from '@/main/models/request';
import { IThreeDotsClaimCancelProps } from '@/main/screens/claim/searchClaim/gridSearchClaim/rowSearchClaim/collapseSearchClaim/headerCollapseSearchClaim/threeDotsClaim/options/threeDotsClaimCancel';
import { useSearchClaimContext } from '@/main/screens/claim/searchClaim/context';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { useCountryReducer } from '@/main/store/country';
import { getReason } from '@/main/store/reason';

import SupplierBulkCancelClaim from './supplierBulkCancelClaim/supplierBulkCancelClaim';
import { ISupplierCancel } from './types';
import { backendToFrontendCancelClaim, getAllPartsForm, getAllPartsFormWithSupplier } from './utilsmodalBulkCancelClaim';
import * as S from './styles';

export const INITIAL_STATE_FORM = {
  suppliers: [] as ISupplierCancel[]
};

export type IFormValues = typeof INITIAL_STATE_FORM;

interface IModalBulkCancelClaimProps extends IThreeDotsClaimCancelProps {
  isOpenModal: boolean
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  closeModal: () => void
}

function ModalBulkCancelClaim({
  isOpenModal,
  guidClaim,
  refreshClaim,
  closeModal
}: IModalBulkCancelClaimProps) {
  const {
    getDataSearchClaim,
  } = useSearchClaimContext();
  const dispatch = useDispatch();
  const { countriesUser: countryReducer } = useCountryReducer();
  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const [isLoadingDataParts, setIsLoadingDataParts] = useState<boolean>(false)
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false)

  const methods = useForm<IFormValues>({
    mode: 'all',
    defaultValues: INITIAL_STATE_FORM
  });
  const {
    control,
    handleSubmit
  } = methods;

  const watcherOptions = useWatch({
    control,
    name: 'suppliers'
  });

  const {
    fields,
    replace
  } = useFieldArray({
    control,
    name: 'suppliers'
  });
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watcherOptions[index]
  }));

  function loadPartsByClaim() {
    setIsLoadingDataParts(true);
    const request: GetBulkChangeItemsByClaimRequest = {
      guidClaim,
      requestedStatus: 8
    };

    procurementApi
      .getBulkChangeItemsByClaim(request)
      .then((response) => {
        const { data: { result } } = response
        const optionsSelected = backendToFrontendCancelClaim(result);

        replace(optionsSelected);
      })
      .finally(() => {
        setIsLoadingDataParts(false)
      })
  }
  function validateForm(data: IFormValues) {
    const allParts = getAllPartsForm(data);
    const hasOneSelected = allParts.some(i => i.isSelected);

    if (!hasOneSelected) {
      const message = getTranslateByKey('information.selectOne', true).replace('{0}', getTranslateByKey('information.part').toLowerCase());
      toast.error(message)
      return false
    }

    return true;
  }

  async function onSubmit(data: IFormValues) {
    if (!validateForm(data)) return;

    const isConfirmModal = await getConfirmation(getTranslateByKey('information.areYouSureYouWantToCancelTheParts'));

    if (!isConfirmModal) return;
    const request: ProcurementProcessStatusBulkRequest[] = [];
    setIsLoadingSave(true);
    const allPartsSelected = getAllPartsFormWithSupplier(data);
    allPartsSelected.forEach(part => {

      request.push({
        confirm: false,
        guidReason: isNullUndefined(part.supplier.reason) ? '' : String(part.supplier.reason.key),
        idReason: Number(part.supplier.reason?.enum),
        reasonDescription: part.supplier.reasonDescription,
        deliveryBySupplier: part.deliveryBySupplier,
        guidProcurement: part.guidProcurement,
        guidProcurementSupplier: part.guidProcurementSupplier,
        procurementSupplierItems: [{
          guidProcurementSupplierItem: part.guidProcurementSupplierItem
        }]
      })
    })

    procurementApi
      .cancelBulkItems(request)
      .then(responseApi => {
        if (responseApi && responseApi.data && responseApi.data.result) {
          const resData = responseApi.data.result;
          toast.success(
            getTranslateByKey('information.partsCanceledSuccessfully')
          );
          refreshClaim()
          closeModal();
        }
      })
      .finally(() => {
        setIsLoadingSave(false);
      });
  };

  function loadOptionsReason() {
    const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
    if (isNullUndefined(countryUser)) return;

    const requestReason: ReasonFilterRequest = {
      guidCountry: String(countryUser.key),
      idReasonType: ReasonTypeEnum.Cancel
    };
    dispatch(getReason(requestReason));
  }

  useEffect(() => {
    if (isOpenModal) {
      loadPartsByClaim()
      loadOptionsReason();

    }
  }, [isOpenModal]);

  if (isLoadingDataParts) return <LoadingComponent />;
  if (isEmpty(controlledFields)) return <ResultNotFoundComponent />;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormProvider {...methods}>
        <S.Container>
          {controlledFields.map((field, indexSupplier) => (
            <SupplierBulkCancelClaim
              key={field.id}
              indexSupplier={indexSupplier}
              supplier={field}
            />
          ))}
          <KOGAreaButtonsModal>
            <SecondaryButton
              id="id-searchClaim-btn-save"
              type="submit"
              disabled={isLoadingSave}
              isLoading={isLoadingSave}
            >
              {getTranslateByKey('information.save')}
            </SecondaryButton>
          </KOGAreaButtonsModal>
          <ModalConfirmation />
        </S.Container>
      </FormProvider>
    </form>
  )
}

function WrapperModalBulkCancelClaim(props: IModalBulkCancelClaimProps) {
  return (
    <KogModalSideComponent
      open={props.isOpenModal}
      shouldRenderOpen
      title={getTranslateByKey('information.cancelParts')}
      closeModal={() => props.setIsOpenModal(false)}
    >
      <ModalBulkCancelClaim {...props} />
    </KogModalSideComponent>
  )
}

export default WrapperModalBulkCancelClaim